import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../api/client";
import { useEvent } from "../context/EventProvider";
import { useSettings } from "../settings/context";
import { getConfig } from "../config";
import type { EmailActionKind } from "../emails/send";
export type { EmailActionKind };

/**
 * ═══ L'ÉTAT DE L'EMAIL D'UNE ACTION, EN LECTURE ═══
 *
 * Décidé le 2026-08-28 : la V2 s'appuie UNIQUEMENT sur le nouveau système d'emails —
 * la page « Templates email IA » du back office, où l'email s'écrit en français dans
 * le gabarit de l'événement et se traduit dans les autres langues. Le panneau ne règle
 * donc plus rien ici (fini le compte et les numéros Mailjet par langue) : il DIT si
 * l'email de l'action partira, et dans quelles langues.
 *
 * La vérité reste celle des lignes `Emails`/`EmailLanguages` que l'envoi lit (`v3/send`) :
 * une langue est servie quand sa case est remplie — par l'éditeur IA (`designs`) ou par
 * un numéro Mailjet posé à la main dans l'ancienne page du BO, qui reste honoré.
 */
export const EMAIL_ACTIONS: Record<
  EmailActionKind,
  { id: number; label: string; subject: string; variable?: string }
> = {
  onsite: {
    id: 1,
    label: "Inscription confirmée (présentiel)",
    subject: "la confirmation",
  },
  remote: {
    id: 2,
    label: "Inscription confirmée (distanciel)",
    subject: "la confirmation",
  },
  link: {
    id: 3,
    label: "Lien de connexion",
    subject: "le lien",
    variable: "{{var:token}}",
  },
  badge: { id: 4, label: "Badge à télécharger", subject: "le badge" },
  delegate: { id: 5, label: "Délégation reçue (délégué)", subject: "l'email" },
  delegating: {
    id: 6,
    label: "Délégation envoyée (demandeur)",
    subject: "l'email",
  },
  decline: { id: 7, label: "Réponse négative", subject: "l'email" },
  paid: { id: 8, label: "Achat confirmé", subject: "la confirmation" },
  companionCreated: {
    id: 10,
    label: "Bienvenue (compte créé)",
    subject: "l'email",
  },
  companion: {
    id: 19,
    label: "Inscription confirmée (accompagnant)",
    subject: "la confirmation",
  },
  otp: {
    id: 22,
    label: "Code de connexion",
    subject: "le code",
    variable: "{{var:otp_code}}",
  },
};

type EmailRow = {
  id: number;
  actionId: number | null;
  /** `{ [languageId]: { [typoId | "null"]: templateId } }`, comme la route le rend. */
  templates: Record<string, Record<string, number>>;
  /** Même forme, valeur = design de l'éditeur IA : la case envoie en HTML direct. */
  designs?: Record<string, Record<string, number>>;
  /** Action exclue par l'événement : rien ne part, ni l'email propre ni le défaut. */
  excludedAt?: string | null;
};

/** Un template par défaut Gayakoa (`EmailDesigns.scope='default'`), tel que la route le rend. */
type DefaultDesign = {
  id: number;
  kind: string;
  actionId: number | null;
  languageId: number | null;
  publishedAt: string | null;
};

export function ActionEmail({ action }: { action: EmailActionKind }) {
  const ACTION = EMAIL_ACTIONS[action];
  const event = useEvent();

  const domainId = event?.domainId;
  const eventId = event?.eventId;
  const base = `/domains/${domainId}/events/${eventId}`;
  const enabled = !!event;

  const actions = useQuery({
    queryKey: ["email-actions", domainId, eventId],
    queryFn: ({ signal }) =>
      apiFetch<{ actions?: Array<{ label: string; value: number }> }>(
        `${base}/emails/v3/actions?type=fo`,
        { api: "apix", signal },
      ),
    enabled,
    staleTime: Infinity,
  });
  const emails = useQuery({
    queryKey: ["emails-v3", domainId, eventId],
    queryFn: ({ signal }) =>
      apiFetch<{ emails?: EmailRow[] }>(`${base}/emails/v3?type=fo`, {
        api: "apix",
        signal,
      }),
    enabled,
  });
  /* LES TEMPLATES PAR DÉFAUT GAYAKOA : sans email propre, `v3/send` envoie le défaut publié
     de l'action, aux couleurs de l'événement. Sans les lire, le panneau annonçait « ne
     partira pas » sur un email qui partait (tâche #224). Lecture ouverte à tout compte
     connecté, communs à tous les événements, d'où la clé sans domaine ni événement. */
  const defaults = useQuery({
    queryKey: ["email-defaults"],
    queryFn: ({ signal }) =>
      apiFetch<{ designs?: DefaultDesign[] }>(`/admin/email-defaults`, {
        api: "apix",
        signal,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  /* Les langues de l'ÉVÉNEMENT, déjà chargées par le contexte des réglages — pas une
     requête à nous : la clé de cache ["languages"] appartient au contexte, qui y range
     la liste parsée (leçon apprise : la réutiliser avec une autre forme rend des ids). */
  const { languages } = useSettings();

  /* Par ID, jamais par libellé : les libellés se renomment (le 2026-08-31, un appariement
     par libellé a cassé l'OTP côté api ; ici il aurait rendu le panneau muet). */
  const actionId = actions.data?.actions?.some((a) => a.value === ACTION.id)
    ? ACTION.id
    : null;
  const email =
    actionId === null
      ? null
      : (emails.data?.emails?.find((e) => e.actionId === actionId) ?? null);

  /* Hors d'un événement, rien à dire — et les requêtes désactivées resteraient « en
     attente » pour toujours, donc le message de lecture aussi. */
  if (!event) return null;

  if (actions.isPending || emails.isPending) {
    return <p className="row__h">Lecture de la configuration</p>;
  }

  /* Une lecture qui échoue n'est PAS « l'email ne partira pas » : on ne sait pas, et un
     bandeau rouge pousserait à recréer un email qui existe peut-être. */
  if (actions.isError || emails.isError) {
    return (
      <p className="row__h" role="status">
        Impossible de lire la configuration des emails pour l'instant. Rechargez
        la page ; si ça persiste, vérifiez dans le back office.
      </p>
    );
  }

  /* Qui remplit chaque langue : l'éditeur IA (`designs`) ou un ancien modèle Mailjet.
     TOUTES les typologies comptent : une case posée pour une typologie sert l'envoi
     (c'est même elle que `v3/send` préfère), elle n'est pas « absente ». */
  const filled = (map?: Record<string, Record<string, number>>) =>
    Object.keys(map ?? {}).filter((k) =>
      Object.values(map?.[k] ?? {}).some(Boolean),
    );
  const byDesign = email ? filled(email.designs) : [];
  const byTemplate = email
    ? filled(email.templates).filter((k) => !byDesign.includes(k))
    : [];
  /**
   * L'adresse de la page « Templates email IA » du back office, déduite de celle
   * d'apix : même étage (local, dev, prod), même domaine et même événement.
   */
  const boUrl = (() => {
    const apix = getConfig().apixUrl || "";
    const host = apix.includes("apix.dev.")
      ? "https://app.dev.gayakoa.com"
      : apix.includes("apix.")
        ? "https://app.gayakoa.com"
        : "http://localhost:7549";
    return `${host}/settings/templates/ai?domain=${domainId}&event=${eventId}`;
  })();
  /* `&create=` ouvre la modale de création sur l'action : seulement quand l'email
     n'existe pas encore — pour le modifier, la page suffit, modale fermée. */
  const boCreateUrl = actionId !== null ? `${boUrl}&create=${actionId}` : boUrl;
  /* « Modifier » ouvre directement l'ATELIER de l'email de référence (le design
     français ; les traductions se refont depuis lui). */
  const masterDesignId = (() => {
    const fr = languages.find((l) => /fran/i.test(l.name))?.id;
    const map = email?.designs ?? {};
    const frId = fr !== undefined ? map[String(fr)]?.null : undefined;
    if (frId) return frId;
    const first = Object.values(map).find((v) => v?.null);
    return first?.null;
  })();
  const boEditUrl = masterDesignId
    ? `${boUrl.split("/settings/")[0]}/settings/emails/designs/${masterDesignId}?domain=${domainId}&event=${eventId}`
    : boUrl;

  const languageLabel = (id: string) =>
    languages.find((l) => String(l.id) === id)?.name ?? id;
  const names = (ids: string[]) => ids.map(languageLabel).join(", ");

  /* Exclue dans le back office : `v3/send` ne fait rien partir, pas même le défaut. */
  if (email?.excludedAt) {
    return (
      <div className="otp">
        <StatusBox tone="ko">
          <strong style={{ color: KO, fontSize: 13 }}>
            ✗ Email exclu de l'événement : {ACTION.subject} ne partira pas
          </strong>
          <span style={{ fontSize: 12, opacity: 0.85 }}>
            <a
              href={boUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: KO, textDecoration: "underline" }}
            >
              Le réinclure dans le back office
            </a>{" "}
            (action « {ACTION.label} »).
          </span>
        </StatusBox>
      </div>
    );
  }

  if (byDesign.length > 0 || byTemplate.length > 0) {
    return (
      <div className="otp">
        <StatusBox tone="ok">
          <strong style={{ color: OK, fontSize: 13 }}>
            ✓ Email créé : {ACTION.subject} partira
          </strong>
          <span style={{ fontSize: 12, opacity: 0.85 }}>
            {byDesign.length > 0 && (
              <>
                Éditeur IA : {names(byDesign)} (
                <a
                  href={boEditUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: OK, textDecoration: "underline" }}
                >
                  modifier dans le back office
                </a>
                ).{" "}
              </>
            )}
            {byTemplate.length > 0 && (
              <>
                Ancien système (modèle Mailjet) : {names(byTemplate)}
                {byDesign.length === 0 && (
                  <>
                    {" "}
                    (
                    <a
                      href={boCreateUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: OK, textDecoration: "underline" }}
                    >
                      le recréer au nouveau système
                    </a>
                    )
                  </>
                )}
                .{" "}
              </>
            )}
            {ACTION.variable && (
              <>
                L'email doit afficher <code>{ACTION.variable}</code>.
              </>
            )}
          </span>
        </StatusBox>
      </div>
    );
  }

  /* Pas d'email propre : tout dépend du défaut Gayakoa, qu'on attend seulement ici (un
     email propre se suffit, inutile de le cacher derrière une lecture qui traîne). */
  if (defaults.isPending) {
    return <p className="row__h">Lecture de la configuration</p>;
  }
  if (defaults.isError) {
    return (
      <p className="row__h" role="status">
        Impossible de lire la configuration des emails pour l'instant. Rechargez
        la page ; si ça persiste, vérifiez dans le back office.
      </p>
    );
  }

  /* Même règle que la grille du back office (`sourceOf`) : un défaut publié pour l'action. */
  const defaultDesigns =
    actionId === null
      ? []
      : (defaults.data?.designs ?? []).filter(
          (d) => d.kind !== "layout" && d.actionId === actionId && d.publishedAt,
        );
  // Seules les langues de l'événement : les autres ne concernent pas ses participants.
  const defaultLanguages = languages
    .filter((l) => defaultDesigns.some((d) => d.languageId === l.id))
    .map((l) => l.name);

  if (defaultDesigns.length > 0) {
    return (
      <div className="otp">
        <StatusBox tone="ok">
          <strong style={{ color: OK, fontSize: 13 }}>
            ✓ Email par défaut : {ACTION.subject} partira
          </strong>
          <span style={{ fontSize: 12, opacity: 0.85 }}>
            Aucun email propre à l'événement : c'est l'email par défaut de
            Gayakoa qui sera envoyé, aux couleurs de l'événement
            {defaultLanguages.length > 0 && <> ({defaultLanguages.join(", ")})</>}
            .{" "}
            <a
              href={boCreateUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: OK, textDecoration: "underline" }}
            >
              Le personnaliser dans le back office
            </a>
            .
          </span>
        </StatusBox>
      </div>
    );
  }

  return (
    <div className="otp">
      <StatusBox tone="ko">
        <strong style={{ color: KO, fontSize: 13 }}>
          ✗ Email à créer : {ACTION.subject} ne partira pas
        </strong>
        <span style={{ fontSize: 12, opacity: 0.85 }}>
          <a
            href={boCreateUrl}
            target="_blank"
            rel="noreferrer"
            style={{ color: KO, textDecoration: "underline" }}
          >
            Créer l'email dans le back office
          </a>{" "}
          (« Ajouter un email », action « {ACTION.label} ») : l'IA le génère
          dans le gabarit de l'événement et le traduit.
        </span>
      </StatusBox>
    </div>
  );
}

const OK = "#86efac";
const KO = "#fca5a5";

/** Le cadre de l'état : vert quand l'email partira, rouge quand il ne partira pas. */
function StatusBox({
  tone,
  children,
}: {
  tone: "ok" | "ko";
  children: ReactNode;
}) {
  const rgb = tone === "ok" ? "74, 222, 128" : "248, 113, 113";
  return (
    <div
      role="status"
      style={{
        border: `1px solid rgba(${rgb}, 0.5)`,
        background: `rgba(${rgb}, 0.12)`,
        borderRadius: 8,
        padding: "10px 12px",
        display: "grid",
        gap: 4,
      }}
    >
      {children}
    </div>
  );
}
